import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global 401 interceptor — on token expiry, clear and redirect to /login.
// Installed once at module load so it survives across logins/logouts.
let interceptorInstalled = false;
const installAuthInterceptor = () => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || '';
      const isAuthEndpoint = error?.config?.url?.includes('/auth/login') ||
                             error?.config?.url?.includes('/auth/register');
      if (status === 401 && !isAuthEndpoint) {
        const wasLoggedIn = !!localStorage.getItem('token');
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        if (wasLoggedIn && window.location.pathname !== '/login') {
          const reason = /expired/i.test(detail) ? 'Session expired. Please log in again.' : 'Please log in to continue.';
          toast.error(reason);
          // Preserve where the user was so we can return them after login
          const returnTo = window.location.pathname + window.location.search;
          setTimeout(() => {
            window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
          }, 600);
        }
      }
      return Promise.reject(error);
    }
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Install the global 401 handler once
  installAuthInterceptor();

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    // Don't pass role - backend determines if first user should be admin
    const response = await axios.post(`${API}/auth/register`, { name, email, password, role: 'employee' });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    // Return the actual user data from backend which has correct role
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isEmployee = () => user?.role === 'employee';
  const isStaff = () => user?.role === 'admin' || user?.role === 'employee';

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout,
      isAdmin,
      isEmployee,
      isStaff 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
